import React, { useEffect } from "react";
import "./App.css";
import { Navigate, Routes, Route, useLocation, useNavigate } from "react-router-dom";

import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import Cart from "./pages/Cart";

import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import AiRoomPreviewLauncher from "./components/AiRoomPreviewLauncher";
import AppInstallPrompt from "./components/AppInstallPrompt";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetails from "./pages/ProductDetails";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";

import About from "./pages/About";
import Collections from "./pages/Collections";
import PhotoStories from "./pages/PhotoStories";
import PhotoStoryPost from "./pages/PhotoStoryPost";
import BookUs from "./pages/BookUs";
import Packages from "./pages/Packages";
import PhotoViewer from "./pages/PhotoViewer";
import AlbumGallery from "./pages/AlbumGallery";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Account from "./pages/Account";
import Wishlist from "./pages/Wishlist";
import MyOrders from "./pages/MyOrders";
import MyDownloads from "./pages/MyDownloads";

import AdminLogin from "./pages/AdminLogin";
import ProtectedAdminRoute from "./components/admin/ProtectedAdminRoute";

import AdminDashboard from "./pages/AdminDashboard";
import AdminProducts from "./pages/AdminProducts";
import AdminOrders from "./pages/AdminOrders";
import AdminHeroSlides from "./pages/AdminHeroSlides";
import AdminNewsletter from "./pages/AdminNewsletter";
import AdminCustomers from "./pages/AdminCustomers";
import AdminImageOptimizer from "./pages/AdminImageOptimizer";
import AdminCollections from "./pages/AdminCollections";
import AdminDownloads from "./pages/AdminDownloads";
import AdminPhotoStories from "./pages/AdminPhotoStories";
import AdminUtility from "./pages/AdminUtility";
import AdminCoupons from "./pages/AdminCoupons";

import trackVisitor from "./utils/trackVisitor";
import Licensing from "./pages/Licensing";
import { initializeNativeApp } from "./nativeApp";

function ProtectedAdminPage({ children }) {
  return <ProtectedAdminRoute>{children}</ProtectedAdminRoute>;
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const isAdminRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname === "/admin-login";

  useEffect(() => {
    if (!isAdminRoute) {
      trackVisitor();
    }
  }, [location.pathname, location.search, isAdminRoute]);

  useEffect(() => {
    let cleanup = () => {};
    initializeNativeApp(navigate).then((removeListeners) => { cleanup = removeListeners; });
    return () => cleanup();
  }, [navigate]);

  return (
    <AuthProvider>
      <CartProvider>
        <div className="App">
          <ScrollToTop />

        {!isAdminRoute && <Header />}

        <Routes>
          {/* Main selling pages */}
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductDetails />} />

          {/* Payment pages */}
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />

          {/* Collections */}
          <Route path="/collections" element={<Collections />} />
          <Route path="/collections/:category" element={<AlbumGallery />} />

          {/* Old portfolio links */}
          <Route
            path="/portfolio"
            element={<Navigate to="/collections" replace />}
          />
          <Route path="/portfolio/:category" element={<AlbumGallery />} />

          {/* Main information pages */}
          <Route path="/licensing" element={<Licensing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<BookUs />} />
          <Route path="/book" element={<BookUs />} />
          <Route path="/packages" element={<Packages />} />

          {/* Photo viewer */}
          <Route path="/view" element={<PhotoViewer />} />

          {/* Photo Stories & Wall Art Journal */}
          <Route path="/stories" element={<PhotoStories />} />
          <Route path="/stories/:slug" element={<PhotoStoryPost />} />

          {/* Old Vlog links redirected to Photo Stories */}
          <Route path="/vlog" element={<Navigate to="/stories" replace />} />
          <Route path="/vlog/:slug" element={<PhotoStoryPost />} />

          {/* Account system */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/account" element={<Account />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/my-orders" element={<MyOrders />} />
          <Route path="/my-downloads" element={<MyDownloads />} />

          {/* Temporary public routes */}
          <Route path="/cart" element={<Cart />} />

          {/* Admin Login */}
          <Route path="/admin-login" element={<AdminLogin />} />

          {/* Protected Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedAdminPage>
                <AdminDashboard />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/products"
            element={
              <ProtectedAdminPage>
                <AdminProducts />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/image-optimizer"
            element={
              <ProtectedAdminPage>
                <AdminImageOptimizer />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/orders"
            element={
              <ProtectedAdminPage>
                <AdminOrders />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/hero-slides"
            element={
              <ProtectedAdminPage>
                <AdminHeroSlides />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/newsletter"
            element={
              <ProtectedAdminPage>
                <AdminNewsletter />
              </ProtectedAdminPage>
            }
          />

          {/* Admin pages planned for later */}
          <Route
            path="/admin/collections"
            element={
              <ProtectedAdminPage>
                <AdminCollections />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/customers"
            element={
              <ProtectedAdminPage>
                <AdminCustomers />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/downloads"
            element={
              <ProtectedAdminPage>
                <AdminDownloads />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/photo-stories"
            element={
              <ProtectedAdminPage>
                <AdminPhotoStories />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/vlog-posts"
            element={
              <ProtectedAdminPage>
                <AdminUtility section="vlog" />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/licensing"
            element={
              <ProtectedAdminPage>
                <AdminUtility section="licensing" />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/coupons"
            element={
              <ProtectedAdminPage>
                <AdminCoupons />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/analytics"
            element={
              <ProtectedAdminPage>
                <AdminUtility section="analytics" />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/messages"
            element={
              <ProtectedAdminPage>
                <AdminUtility section="messages" />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/settings"
            element={
              <ProtectedAdminPage>
                <AdminUtility section="settings" />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/users"
            element={
              <ProtectedAdminPage>
                <AdminUtility section="users" />
              </ProtectedAdminPage>
            }
          />

          <Route
            path="/admin/activity-logs"
            element={
              <ProtectedAdminPage>
                <AdminUtility section="activity" />
              </ProtectedAdminPage>
            }
          />

          {/* Safety fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {!isAdminRoute && <AiRoomPreviewLauncher />}
        {!isAdminRoute && <AppInstallPrompt />}
        {!isAdminRoute && <Footer />}
      </div>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
